/**
 * Plan selection -> Stripe Checkout (plan §13.1, §13.2, §23.2).
 *
 * NO AMOUNT IS EVER SENT BY THE CLIENT (§29.2). The browser picks an interval; the API resolves
 * that to a configured Stripe price. A promotion code is passed through by name only.
 *
 * Provisioning does NOT start on the browser's return from Checkout (§13.2) — the webhook owns
 * that — so `successUrl` simply points at the building screen, which polls.
 */
import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, ErrorNote, Spinner } from "../components/ui.js";
import { useSession } from "../state/session.js";

type Interval = "month" | "year";

export default function Plans() {
  const { me } = useSession();
  const [interval, setInterval] = useState<Interval>("month");
  const [promotionCode, setPromotionCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Already has a live Construct — nothing to buy.
  if (me?.deployment && me.deployment.status !== "AWAITING_PAYMENT") {
    return <Navigate to="/dashboard" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { url } = await api.checkout({
        interval,
        ...(promotionCode.trim() ? { promotionCode: promotionCode.trim() } : {}),
        successUrl: `${window.location.origin}/building`,
        cancelUrl: `${window.location.origin}/plans`,
      });
      window.location.href = url;
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  }

  const notImplemented = error instanceof ApiError && error.code === "not_implemented";

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Step 1 of 2</p>
        <h1>Choose how you pay</h1>
        <p className="lede">
          One plan, two billing rhythms. Everything is included: your private vault, durable
          versioned Markdown, search and recall, and full export whenever you want it.
        </p>
      </div>

      {notImplemented ? (
        <Banner kind="warn" title="Checkout is not live yet">
          Plan selection is wired up, but billing is still being finished. Nothing was charged.
        </Banner>
      ) : error ? (
        <ErrorNote error={error} />
      ) : null}

      <form onSubmit={onSubmit}>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
          {(
            [
              { value: "month" as const, title: "Monthly", note: "Billed every month. Cancel any time." },
              { value: "year" as const, title: "Annual", note: "Billed once a year. Two months on us." },
            ]
          ).map((option) => (
            <label key={option.value} className="choice" data-selected={interval === option.value}>
              <input
                type="radio"
                name="interval"
                value={option.value}
                checked={interval === option.value}
                onChange={() => setInterval(option.value)}
              />
              <span className="choice__title">{option.title}</span>
              <span className="choice__note">{option.note}</span>
            </label>
          ))}
        </div>

        <div className="card" style={{ marginTop: 20 }}>
          <label className="field" style={{ marginTop: 0 }}>
            <span className="field__label">Promotion code (optional)</span>
            <input
              className="input"
              type="text"
              name="promotionCode"
              autoComplete="off"
              placeholder="LAUNCH50"
              value={promotionCode}
              onChange={(e) => setPromotionCode(e.target.value.toUpperCase())}
            />
            <span className="field__hint">If you were given a code, enter it here. It is applied at checkout.</span>
          </label>
        </div>

        <div className="btn-row">
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? <Spinner label="Opening checkout" /> : null}
            {submitting ? "Opening checkout" : "Continue to checkout"}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 28 }}>
        <Banner kind="info" title="What happens next">
          You will be taken to our payment provider. When you come back, we start building your
          Construct. It usually takes a couple of minutes, and you can close the tab.
        </Banner>
      </div>
    </>
  );
}
